/**
 * CooperScenes Configuration
 * Supports two deployment environments:
 * - Anonymized (non-branded) environment for November 2025 launch
 * - UCR branded environment for March 2026 launch
 */

// Environment detection based on build mode or hostname
const ENV = import.meta.env.VITE_BRAND_ENV || 'anonymized';

// Configuration for each environment
const configs = {
  anonymized: {
    branding: {
      siteName: 'CooperScene Visualizer & Download',
      tagline: '',
      showLogo: false,
      // Matches the CooperScene project page's palette (static/css/index.css).
      primaryColor: '#2c3e50',
      accentColor: '#3498db',
      // Same nav items as the CooperScene project page's own navbar,
      // pointing back at that page's sections, plus the one item that's
      // specific to this viewer (Collaborators has no equivalent there).
      nav: [
        { label: 'Data Preview', href: 'https://cisl.ucr.edu/CooperScene#data-preview' },
        { label: 'Overview', href: 'https://cisl.ucr.edu/CooperScene#overview' },
        { label: 'Benchmark', href: 'https://cisl.ucr.edu/CooperScene#benchmark' },
        { label: 'Download', href: './#downloads-section' },
        { label: 'Citation', href: 'https://cisl.ucr.edu/CooperScene#citation' },
        { label: 'Collaborators', href: './collaborators.html' }
      ]
    },
    footer: {
      copyright: '',
      showInstitution: false
    }
  },

  ucr: {
    branding: {
      siteName: 'CooperScene Visualizer & Download',
      tagline: 'The CooperScene Dataset Visualization tool is a collaborative project developed by CISL@UCR and UCR Library Research Services.',
      stamp: 'Built by UCR Library',
      showLogo: false,
      // Matches the CooperScene project page's palette; true UCR Blue/Gold
      // (--ucr-blue/--ucr-gold in main.css) are kept separately for the
      // institutional stamp and footer logo chip.
      primaryColor: '#2c3e50',
      accentColor: '#3498db',
      // Same nav items as the CooperScene project page's own navbar,
      // pointing back at that page's sections, plus the one item that's
      // specific to this viewer (Collaborators has no equivalent there).
      nav: [
        { label: 'Data Preview', href: 'https://cisl.ucr.edu/CooperScene#data-preview' },
        { label: 'Overview', href: 'https://cisl.ucr.edu/CooperScene#overview' },
        { label: 'Benchmark', href: 'https://cisl.ucr.edu/CooperScene#benchmark' },
        { label: 'Download', href: './#downloads-section' },
        { label: 'Citation', href: 'https://cisl.ucr.edu/CooperScene#citation' },
        { label: 'Collaborators', href: './collaborators.html' }
      ]
    },
    footer: {
      copyright: '© 2026 University of California, Riverside',
      attribution: 'This visualization site is a collaborative initiative directed by CISL@UCR and developed by UCR Library Research Services.',
      logo: {
        path: './assets/ucr-library-logo.png',
        text: 'UC Riverside Library Research Services',
        link: 'https://library.ucr.edu'
      },
      showInstitution: true,
      institutionLink: 'https://www.ucr.edu'
    }
  }
};

// Export active configuration
export const config = configs[ENV];
export const environment = ENV;

/**
 * RRD file configurations
 * These will be loaded from AWS S3 (shared between both environments)
 */
export const rrdFiles = [
    {
    id: 'scene-6',
    title: 'scene_6',
    description: 'LIDAR computer vision object detection and tracking - scene 6',
    url: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_6_preview.rrd',
    thumbnail: './assets/thumbnails/scene-3.jpg',
    datasetUrl: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_6.mcap'
  },
  {
    id: 'scene-7',
    title: 'scene_7',
    description: 'LIDAR computer vision object detection and tracking - scene 7',
    url: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_7_preview.rrd',
    thumbnail: './assets/thumbnails/scene-1.jpg',
    datasetUrl: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_7.mcap'
  },
  {
    id: 'scene-8',
    title: 'scene_8',
    description: 'LIDAR computer vision object detection and tracking - scene 8',
    url: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_8_preview.rrd',
    thumbnail: './assets/thumbnails/scene-2.jpg',
    datasetUrl: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_8.mcap'
  },
  {
    id: 'scene-9',
    title: 'scene_9',
    description: 'LIDAR computer vision object detection and tracking - scene 9',
    url: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_9_preview.rrd',
    thumbnail: './assets/thumbnails/scene-3.jpg',
    datasetUrl: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_9.mcap'
  },
  {
    id: 'scene-10',
    title: 'scene_10',
    description: 'LIDAR computer vision object detection and tracking - scene 10',
    url: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_10_preview.rrd',
    thumbnail: './assets/thumbnails/scene-3.jpg',
    datasetUrl: 'http://ucr-library-cooperscenes-data.s3-website-us-east-1.amazonaws.com/scene_10.mcap'
  },
];

/**
 * Viewer settings
 */
export const viewerSettings = {
  defaultWidth: '100%',
  defaultHeight: '800px',
  enableTimeline: true,
  enableSelection: true
};
